import React, { useMemo, useState, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { useUserContext } from 'providers/UserProvider';
import Table from 'components/common/Table';
import { gatePassApi, grnApi } from 'api';
import { getGatePassListColumns, GATEPASS_STATUS } from 'constants/gatepass';
import GrnCreateModal from 'components/purchase-order/purchase-order/GrnCreateModal';
import paths from 'routes/paths';
import { toastApiError } from 'utils/apiErrors';

const ReceivedListPage = () => {
  const navigate = useNavigate();
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const [showGrnCreateModal, setShowGrnCreateModal] = useState(false);
  const [selectedGatePass, setSelectedGatePass] = useState(null);

  const openGrnCreateModal = gatePass => {
    setSelectedGatePass(gatePass);
    setShowGrnCreateModal(true);
  };

  const closeGrnCreateModal = () => {
    setShowGrnCreateModal(false);
    setSelectedGatePass(null);
  };

  const handleCreateGRN = async () => {
    if (!selectedGatePass) return;

    try {
      const response = await grnApi.create({
        gate_pass: selectedGatePass.id,
      });
      toast.success('GRN created successfully!');
      closeGrnCreateModal();
      navigate(paths.grns.details(tenant, warehouse, response.id));
    } catch (error) {
      toastApiError(error, 'Failed to create GRN.');
    }
  };

  const columns = useMemo(() => {
    const createColumn = {
      id: 'create',
      header: 'Action',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-center' },
        cellProps: { className: 'text-center' },
      },
      cell: ({ row: { original } }) => {
        const { id, gate_pass_status } = original;

        const showCreate = gate_pass_status !== GATEPASS_STATUS.CREATED;

        if (!showCreate) return null;

        return (
          <Button
            variant="primary"
            size="sm"
            className="text-white"
            onClick={() => openGrnCreateModal(original)}
          >
            Create GRN
          </Button>
        );
      },
    };

    return [...getGatePassListColumns(tenant, warehouse), createColumn];
  }, [tenant, warehouse]);

  const tableRef = useRef(null);

  return (
    <>
      <Table
        ref={tableRef}
        title="Received List"
        apiService={gatePassApi}
        fetchMethod="getReceivedList"
        columns={columns}
        sortable
        fetchConfig={{ method: 'getReceivedList' }}
        customConfig={{
          transformResponse: response => {
            const filteredResults = response.results.filter(
              item => item.gate_pass_status !== GATEPASS_STATUS.CREATED,
            );
            return {
              data: filteredResults,
              totalItems:
                response.count || response.totalItems || filteredResults.length,
            };
          },
        }}
      />
      <GrnCreateModal
        show={showGrnCreateModal}
        onHide={closeGrnCreateModal}
        onConfirm={handleCreateGRN}
        selectedItem={selectedGatePass}
        type="gatepass"
      />
    </>
  );
};

export default ReceivedListPage;
